import { Controller } from '@hotwired/stimulus'
import { useLazyLoad, loadTurboFrame } from '../utils/use_lazy_load'

/**
 * Lazy Modal Controller
 * 
 * Loads a Turbo Frame's content when a Bootstrap modal is shown.
 * Prevents loading heavy content until the user actually opens the modal.
 *
 * @controller lazy-modal
 * @target {HTMLElement} frame - The turbo-frame element to load
 * @value {String} src - URL to load into the frame
 *
 * @example Modal with lazy-loaded content
 * <div class="modal" id="myModal"
 *      data-controller="lazy-modal"
 *      data-lazy-modal-src-value="/some/content">
 *   <turbo-frame id="modal-content" data-lazy-modal-target="frame">
 *     Loading...
 *   </turbo-frame>
 * </div>
 */
export default class extends Controller {
  static targets = ['frame']
  static values = { src: String }

  connect() {
    useLazyLoad(this, {
      event: 'show.bs.modal',
      load: () => this.load()
    })
    
    // Listen for turbo frame load completion to initialize select2
    if (this.hasFrameTarget) {
      this.boundOnFrameLoad = this.onFrameLoad.bind(this)
      this.frameTarget.addEventListener('turbo:frame-load', this.boundOnFrameLoad)
    }
  }

  disconnect() {
    if (this.hasFrameTarget && this.boundOnFrameLoad) {
      this.frameTarget.removeEventListener('turbo:frame-load', this.boundOnFrameLoad)
    }
  }

  load() {
    if (this.hasFrameTarget && this.hasSrcValue) {
      loadTurboFrame(this.frameTarget, this.srcValue)
    }
  }

  onFrameLoad() {
    // Initialize select2 on any selects in the frame (legacy support)
    if (typeof $ !== 'undefined' && $.fn.select2) {
      $(this.frameTarget).find('select').select2()
    }
  }
}
